import type { SVGProps } from "react";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "fill"> {
  size?: number;
}

/**
 * Single source for inline icons. Stroked, 1.6 weight, 24×24 viewBox,
 * `currentColor` for stroke so they inherit text color from the parent.
 *
 * When we eventually take a real dep (lucide-react), this file is the only
 * surface to swap — every consumer imports named icons from here.
 */
function svg(size: number, extra: IconProps) {
  const { className, ...rest } = extra;
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": true as const,
    className,
    ...rest,
  };
}

export function PlusIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export function KeyIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <circle cx="8.5" cy="14.5" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M11 12.2 19 4M16.5 6.5 18 8M14 9l2 2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PowerIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M12 4v8M6.4 7.6a8 8 0 1 0 11.2 0"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function PauseIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <rect x="6" y="5" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.6" />
      <rect x="14" y="5" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

export function PlayIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path d="M7 5l11 7-11 7V5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

export function CheckIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="m5 12 5 5L20 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function XIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ArrowRightIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M5 12h14M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ArrowLeftIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M19 12H5M11 5l-7 7 7 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ExternalLinkIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M14 5h5v5M19 5l-9 9M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function LogOutIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4M16 17l5-5-5-5M21 12H9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function RefreshIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M3 12a9 9 0 0 1 15.5-6.3L21 8M21 4v4h-4M21 12a9 9 0 0 1-15.5 6.3L3 16M3 20v-4h4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PlugIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M9 3v5M15 3v5M7 8h10v3a5 5 0 0 1-10 0V8ZM12 16v5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function LayersIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="m12 3 9 5-9 5-9-5 9-5ZM3 12l9 5 9-5M3 17l9 5 9-5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ChevronDownIcon({ size = 14, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AlertIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M12 3 2.5 19.5h19L12 3ZM12 10v4M12 17.5h.01"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function TrashIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PencilIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M4 20h4l11.5-11.5a2.12 2.12 0 0 0-3-3L5 17v3ZM14.5 6.5l3 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SunIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function MoonIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <path
        d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MonitorIcon({ size = 16, ...rest }: IconProps) {
  return (
    <svg {...svg(size, rest)}>
      <rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
